import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchGameConfig,
  fetchGameSaves,
  putGameConfig,
  type GameConfigField,
} from '../../api/gameConfig';
import { ConfigField } from './ConfigField';
import type { GameItem } from '../../api/games';

interface Props {
  game: GameItem | null;
  onClose: () => void;
}

type SaveMode = 'save-restart' | 'save-only';

function RestartConfirmDialog({
  open,
  onChoice,
}: {
  open: boolean;
  onChoice: (mode: SaveMode | null) => void;
}) {
  return (
    <Dialog open={open} maxWidth="xs" fullWidth>
      <DialogTitle>Einstellungen speichern</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          Einige geänderte Felder erfordern einen Neustart, damit sie wirksam werden. Was möchtest du tun?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, pb: 2, px: 2 }}>
        <Button variant="contained" onClick={() => onChoice('save-restart')} fullWidth>
          Speichern und neu starten
        </Button>
        <Button variant="outlined" onClick={() => onChoice('save-only')} fullWidth>
          Nur speichern (kein Neustart)
        </Button>
        <Button onClick={() => onChoice(null)} fullWidth>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function GameConfigDialog({ game, onClose }: Props) {
  const qc = useQueryClient();
  const [changedValues, setChangedValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);

  const { data: configData, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['game-config', game?.id],
    queryFn: () => fetchGameConfig(game!.id),
    enabled: !!game,
    staleTime: 0,
  });

  const { data: savesData } = useQuery({
    queryKey: ['game-saves', game?.id],
    queryFn: () => fetchGameSaves(game!.id),
    enabled: !!game && configData?.supports_config === true,
    staleTime: 30_000,
  });

  // Reset dirty state when dialog opens for a new game
  useEffect(() => {
    setChangedValues({});
    setSaveError(null);
    setSavedMessage(null);
  }, [game?.id]);

  if (!game) return null;

  const schema: GameConfigField[] = configData?.schema ?? [];
  const baseValues: Record<string, unknown> = configData?.values ?? {};
  const saves = (savesData?.saves ?? []).map((s) => s.name);

  const mergedValues: Record<string, unknown> = { ...baseValues, ...changedValues };
  const isDirty = Object.keys(changedValues).length > 0;

  const importantFields = schema.filter((f) => f.important);
  const advancedFields = schema.filter((f) => !f.important);

  const changedKeys = Object.keys(changedValues).filter(
    (k) => changedValues[k] !== baseValues[k],
  );
  const anyRequiresRestart = changedKeys.some((k) => schema.find((f) => f.key === k)?.requiresRestart);

  const handleFieldChange = (key: string, value: unknown) => {
    setChangedValues((prev) => ({ ...prev, [key]: value }));
    setSavedMessage(null);
  };

  const handleSaveClick = () => {
    if (changedKeys.length === 0) return;
    if (anyRequiresRestart) {
      setRestartConfirmOpen(true);
    } else {
      void doSave(false);
    }
  };

  const handleRestartChoice = (mode: SaveMode | null) => {
    setRestartConfirmOpen(false);
    if (mode !== null) void doSave(mode === 'save-restart');
  };

  const doSave = async (restart: boolean) => {
    if (!game) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await putGameConfig(game.id, mergedValues, restart);
      setChangedValues({});
      void qc.invalidateQueries({ queryKey: ['game-config', game.id] });
      setSavedMessage(
        result.restarted
          ? 'Gespeichert und Server neu gestartet.'
          : `Gespeichert (${result.changed_keys.length} Feld${result.changed_keys.length !== 1 ? 'er' : ''} geändert).`,
      );
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Speichern fehlgeschlagen';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (isDirty) {
      if (!window.confirm('Ungespeicherte Änderungen verwerfen?')) return;
    }
    onClose();
  };

  return (
    <>
      <Dialog open={!!game} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Game Config — {game.name}</DialogTitle>
        <DialogContent>
          {configLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {configError && (
            <Alert severity="error">Konfiguration konnte nicht geladen werden.</Alert>
          )}

          {configData && !configData.supports_config && (
            <Alert severity="info">
              Dieser Game-Provider unterstützt keinen Config-Editor.
            </Alert>
          )}

          {configData?.supports_config && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {importantFields.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    Wichtige Einstellungen
                  </Typography>
                  {importantFields.map((f) => (
                    <ConfigField
                      key={f.key}
                      field={f}
                      value={mergedValues[f.key]}
                      saves={saves}
                      onChange={(v) => handleFieldChange(f.key, v)}
                      disabled={saving}
                    />
                  ))}
                </>
              )}

              {advancedFields.length > 0 && (
                <>
                  <Divider />
                  <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Erweitert
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Stack spacing={2}>
                        {advancedFields.map((f) => (
                          <ConfigField
                            key={f.key}
                            field={f}
                            value={mergedValues[f.key]}
                            saves={saves}
                            onChange={(v) => handleFieldChange(f.key, v)}
                            disabled={saving}
                          />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              {saveError && <Alert severity="error">{saveError}</Alert>}
              {savedMessage && <Alert severity="success">{savedMessage}</Alert>}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Schließen
          </Button>
          {configData?.supports_config && (
            <Button
              variant="contained"
              disabled={saving || !isDirty || changedKeys.length === 0}
              onClick={handleSaveClick}
              startIcon={saving ? <CircularProgress size={16} /> : undefined}
            >
              Speichern
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <RestartConfirmDialog open={restartConfirmOpen} onChoice={handleRestartChoice} />
    </>
  );
}
